import React, { useEffect } from 'react';
import {
  DEFAULT_DESCRIPTION,
  DEFAULT_OG_IMAGE,
  DEFAULT_TITLE,
  SITE_NAME,
  SITE_URL,
  absoluteUrl,
  pageTitle,
} from '@config/site';

type SeoHeadProps = {
  title?: string;
  description?: string;
  path?: string;
  image?: string;
  noindex?: boolean;
  type?: 'website' | 'article';
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
};

const upsertMeta = (attr: 'name' | 'property', key: string, content: string) => {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

const upsertLink = (rel: string, href: string) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const JSON_LD_ID = 'roomza-json-ld';

/**
 * Client-side document head updates for SPA routes.
 * Initial HTML in index.html covers the first paint / non-JS crawlers.
 */
const SeoHead: React.FC<SeoHeadProps> = ({
  title,
  description = DEFAULT_DESCRIPTION,
  path = '/',
  image = DEFAULT_OG_IMAGE,
  noindex = false,
  type = 'website',
  jsonLd,
}) => {
  const jsonLdSerialized = jsonLd ? JSON.stringify(jsonLd) : '';

  useEffect(() => {
    const fullTitle = title ? pageTitle(title) : DEFAULT_TITLE;
    const url = absoluteUrl(path);
    const robots = noindex ? 'noindex, nofollow' : 'index, follow, max-image-preview:large';

    document.title = fullTitle;

    upsertMeta('name', 'description', description);
    upsertMeta('name', 'robots', robots);
    upsertMeta('name', 'googlebot', robots);
    upsertMeta('property', 'og:title', fullTitle);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:url', url);
    upsertMeta('property', 'og:image', image);
    upsertMeta('property', 'og:type', type);
    upsertMeta('property', 'og:site_name', SITE_NAME);
    upsertMeta('property', 'og:locale', 'en_ZA');
    upsertMeta('name', 'twitter:card', 'summary_large_image');
    upsertMeta('name', 'twitter:title', fullTitle);
    upsertMeta('name', 'twitter:description', description);
    upsertMeta('name', 'twitter:image', image);
    upsertLink('canonical', url);

    const existing = document.getElementById(JSON_LD_ID);
    if (existing) existing.remove();

    if (jsonLdSerialized) {
      const script = document.createElement('script');
      script.id = JSON_LD_ID;
      script.type = 'application/ld+json';
      script.text = jsonLdSerialized;
      document.head.appendChild(script);
    }

    return () => {
      const script = document.getElementById(JSON_LD_ID);
      if (script) script.remove();
    };
  }, [title, description, path, image, noindex, type, jsonLdSerialized]);

  return null;
};

export const organizationJsonLd = () => ({
  '@type': 'Organization',
  name: SITE_NAME,
  legalName: 'Roomzaa (Pty) Ltd',
  url: SITE_URL,
  logo: `${SITE_URL}/Za.png`,
  email: 'Shaun@roomzaaa.co.za',
  areaServed: {
    '@type': 'Country',
    name: 'South Africa',
  },
  sameAs: [SITE_URL],
});

export const websiteJsonLd = () => ({
  '@type': 'WebSite',
  name: SITE_NAME,
  url: SITE_URL,
  description: DEFAULT_DESCRIPTION,
  inLanguage: 'en-ZA',
  publisher: {
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
  },
});

export default SeoHead;
